import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import {
  Observable,
  Subscription,
  catchError,
  debounceTime,
  distinctUntilChanged,
  map,
  merge,
  of,
  startWith,
  switchMap,
  tap,
} from 'rxjs';
import { API, ReportInputTypes } from 'src/app/shared/models/common';
import { ApprovalState } from 'src/app/shared/dto/ApprovalState';
import {
  ActionBoardScope,
  ActionBoardService,
} from '../services/action-board.service';
import { TopFilterService } from '../services/topFIlter.service';
import { UtilityService } from 'src/app/utils/utility.service';

export type ActionBoardItemKey =
  | 'pendingClients'
  | 'pendingLoanApplications'
  | 'pendingDisbursements'
  | 'pendingDepositAccounts'
  | 'openSupportTickets';

export interface ActionBoardItem {
  key: ActionBoardItemKey;
  /** Card label, e.g. "Pending Clients". Localized at declaration. */
  name: string;
  /** Accessible "Open <name>" label, precomputed once (see setOpenLabels) so the
   *  template binds a property instead of calling $localize every change-detection
   *  cycle. */
  openLabel?: string;
  /** Count awaiting action; null when the count could not be fetched. */
  value: number | null;
  /** Registered mat-icon name (see CustomIconsService). */
  icon: string;
  /** Where the card takes the user. */
  route: string;
  /**
   * Query params that land the target list on its Pending tab.
   *
   * The list pages (clients / loan accounts / deposit accounts) all share the
   * same routerWatcher contract: they REPLACE their internal pageRequest with
   * the URL params wholesale, and bail out entirely when `state` is absent.
   * So each entry below carries the complete param set that page's own
   * `onStateChange('pending')` would produce — a partial set would leave the
   * target with no page/pageSize and a NaN pagination state.
   */
  queryParams?: { [key: string]: string | number };
  isFetching: boolean;
}

@Component({
  selector: 'app-action-board',
  templateUrl: './action-board.component.html',
  styleUrls: ['./action-board.component.css'],
})
export class ActionBoardComponent implements OnInit, OnDestroy {
  private subscriptions = new Subscription();

  public title = $localize`:Action Needed!|heading of the dashboard action board@@action_needed:Action Needed!`;

  public items: ActionBoardItem[] = [
    {
      key: 'pendingClients',
      name: $localize`:Pending Clients|action board card label@@pending_clients:Pending Clients`,
      value: null,
      icon: 'action_pending_clients',
      route: '/clients/all-clients',
      queryParams: {
        page: 0,
        pageSize: 10,
        state: ApprovalState.PENDING,
        sortField: 'last_update_time',
        sortOrder: 'DESC',
        filter: '',
      },
      isFetching: false,
    },
    {
      key: 'pendingLoanApplications',
      name: $localize`:Pending Loan Applications|action board card label@@pending_loan_applications:Pending Loan Applications`,
      value: null,
      icon: 'action_pending_loan_applications',
      route: '/loans/loan-accounts',
      // NB: no loanState — the loan list's pending tab deletes it, and sending
      // loanState=APPROVED alongside state=PENDING yields an empty result.
      queryParams: {
        page: 0,
        pageSize: 10,
        state: ApprovalState.PENDING,
        sortField: 'last_update_time',
        sortOrder: 'DESC',
        filter: '',
        loanAccountType: 'INDIVIDUAL',
      },
      isFetching: false,
    },
    {
      key: 'pendingDisbursements',
      name: $localize`:Pending Disbursements|action board card label@@pending_disbursements:Pending Disbursements`,
      value: null,
      icon: 'action_pending_disbursements',
      // Already the awaiting-disbursement queue: no state tabs, and it reads
      // only loanAccountType off the URL, so no params to pass.
      route: '/loans/postings/bulk-disbursement',
      isFetching: false,
    },
    {
      key: 'pendingDepositAccounts',
      name: $localize`:Pending Deposit Accounts|action board card label@@pending_deposit_accounts:Pending Deposit Accounts`,
      value: null,
      icon: 'action_pending_deposit_accounts',
      route: '/deposits/all-deposit',
      queryParams: {
        page: 0,
        pageSize: 10,
        state: ApprovalState.PENDING,
        sortField: 'last_update_time',
        sortOrder: 'DESC',
        filter: '',
        savingsAccountType: 'INDIVIDUAL',
      },
      isFetching: false,
    },
    {
      key: 'openSupportTickets',
      name: $localize`:Open Support Tickets|action board card label@@open_support_tickets:Open Support Tickets`,
      value: null,
      icon: 'action_open_support_tickets',
      // The support page lands on open tickets already.
      route: '/support',
      isFetching: false,
    },
  ];

  private topFiltersFormInstance: FormGroup;

  constructor(
    private service: ActionBoardService,
    private topFilterService: TopFilterService,
    private utils: UtilityService,
  ) {
    this.topFiltersFormInstance = this.topFilterService.topFilterForm;
  }

  /**
   * Accessible name and tooltip for the corner link — the card body is inert,
   * so the link is the only thing a screen reader or keyboard user can reach
   * and it needs to say where it goes.
   */
  /**
   * Precompute the accessible "Open <name>" labels once. Previously openLabel(item)
   * was called from the template on both aria-label and matTooltip, so $localize ran
   * on every change-detection cycle per card; with a missing translation that flooded
   * the console with "No translation found" warnings and froze the tab.
   */
  private setOpenLabels(): void {
    this.items.forEach(
      (item) =>
        (item.openLabel = $localize`:@@action_board_open_link:Open ${item.name}`),
    );
  }

  ngOnInit(): void {
    this.setOpenLabels();
    this.fetchCounts();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  /**
   * Re-runs the counts whenever the dashboard's area / branch / account-officer
   * or date-range filters change, and once on load.
   *
   * distinctUntilChanged still guards the wire — the form emits on every
   * control touch, so it compares the resolved scope and refetches only when
   * one of the five values actually moved. switchMap drops the in-flight
   * requests of a superseded filter selection so a slow earlier response can't
   * land after a newer one.
   */
  private fetchCounts(): void {
    this.subscriptions.add(
      this.topFiltersFormInstance.valueChanges
        .pipe(
          startWith(null),
          debounceTime(300),
          map(() => this.currentScope()),
          distinctUntilChanged(
            (a, b) =>
              a.areaId === b.areaId &&
              a.branchId === b.branchId &&
              a.fieldOfficerId === b.fieldOfficerId &&
              a.startDate === b.startDate &&
              a.endDate === b.endDate,
          ),
          tap((scope) => this.scopeClientsLink(scope)),
          switchMap((scope) => {
            this.items.forEach((item) => (item.isFetching = true));
            return merge(...this.countRequests(scope));
          }),
        )
        .subscribe(({ key, value }) => this.setCount(key, value)),
    );
  }

  /* Pending Clients counts within the dashboard's area / branch, so its link
     carries them too: the clients list then opens on the same clients the
     card counted, not every area's. 0 (All) is left out of the link. */
  private scopeClientsLink(scope: ActionBoardScope): void {
    const item = this.items.find((i) => i.key === 'pendingClients');
    if (!item?.queryParams) return;
    const params = { ...item.queryParams };
    delete params['areaId'];
    delete params['branchId'];
    item.queryParams = {
      ...params,
      ...(scope.areaId ? { areaId: scope.areaId } : {}),
      ...(scope.branchId ? { branchId: scope.branchId } : {}),
    };
  }

  /**
   * The top-filter selects sit at 0 ("all") when nothing is picked, which is
   * exactly what these endpoints expect, so the value passes straight through.
   */
  private currentScope(): ActionBoardScope {
    const filters = this.topFiltersFormInstance.value;
    return {
      areaId: Number(filters?.[ReportInputTypes.AREA]) || 0,
      branchId: Number(filters?.[ReportInputTypes.BRANCH]) || 0,
      // the form calls it loanOfficerId; the endpoints call it fieldOfficerId
      fieldOfficerId: Number(filters?.[ReportInputTypes.LOAN_OFFICER]) || 0,
      startDate: this.scopeDate(filters?.[ReportInputTypes.START_DATE]),
      endDate: this.scopeDate(filters?.[ReportInputTypes.END_DATE]),
    };
  }

  /**
   * Dates go over the wire as yyyy-MM-dd, formatted in LOCAL time —
   * toISOString() would roll the day back for anyone east of UTC in the
   * evening and silently query the wrong range.
   *
   * Falls back to today rather than sending "Invalid date" if a control is
   * cleared or holds something unparseable.
   */
  private scopeDate(value: Date | string | null | undefined): string {
    const parsed = value ? new Date(value) : new Date();
    const safe = Number.isNaN(parsed.getTime()) ? new Date() : parsed;
    return this.utils.formatDate_custom_yearFirst(safe);
  }

  /**
   * One independent stream per card, so each fills in as it lands rather than
   * all five waiting on the slowest, and one dead endpoint costs only its card.
   */
  private countRequests(
    scope: ActionBoardScope,
  ): Observable<{ key: ActionBoardItemKey; value: number | null }>[] {
    const requests: [ActionBoardItemKey, Observable<API<number>>][] = [
      ['pendingClients', this.service.getPendingClientsCount(scope)],
      [
        'pendingLoanApplications',
        this.service.getPendingLoanApplicationsCount(scope),
      ],
      [
        'pendingDisbursements',
        this.service.getPendingDisbursementsCount(scope),
      ],
      [
        'pendingDepositAccounts',
        this.service.getPendingDepositAccountsCount(scope),
      ],
      ['openSupportTickets', this.service.getOpenSupportTicketsCount()],
    ];

    return requests.map(([key, request]) =>
      request.pipe(
        map((res) => ({ key, value: this.toCount(res) })),
        // Leave the count null so the card shows a dash: a failed request is
        // not the same as "nothing to action", and must not read as 0.
        catchError(() => of({ key, value: null as number | null })),
      ),
    );
  }

  /**
   * The count sits directly in `data` (API<number>). Guard the shape anyway —
   * a 200 with a malformed body should degrade to a dash, not render "null".
   */
  private toCount(res: API<number>): number | null {
    const value = Number(res?.data);
    return Number.isFinite(value) ? value : null;
  }

  private setCount(key: ActionBoardItemKey, value: number | null): void {
    const item = this.items.find((val) => val.key === key);
    if (!item) return;
    item.value = value;
    item.isFetching = false;
  }
}
